import { AddAPhoto, Cancel, Edit, Save } from "@mui/icons-material"
import {
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  TextField,
  Typography,
} from "@mui/material"
import { StyledButton } from "components/Button/StyledButton"
import { CameraDialog } from "components/Camera/CameraDialog"
import { ImageSlider } from "components/ImageSlider/ImagesSlider"
import { DeviceEutGroup, File, FileType, FileUploadInput, User } from "graphql/types"
import { useStoreActions } from "hooks/storeHooks"
import { enqueueSnackbar } from "notistack"
import { FormEvent, useEffect, useState } from "react"
import { emptyPerson } from "store/store"
import { useLocalStorage } from "utils/useLocalStorage"
import { v4 } from "uuid"

type EUTGaleryBoxProps = {
  eut: DeviceEutGroup
  onSave: (arg: DeviceEutGroup) => void
}

export const EUTGaleryBox = (props: EUTGaleryBoxProps) => {
  const { eut, onSave } = props
  const { uploadFile } = useStoreActions((actions) => actions)

  const [imagesForSlider, setImagesForSlider] = useState<File[]>(
    [...eut.images, ...eut.devices.flatMap((item) => item.images)].filter(
      (image) => image !== undefined
    )
  )
  const [openCameraDialog, setOpenCameraDialog] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [user] = useLocalStorage<User>("user", emptyPerson)
  const [newDevice, setNewDevice] = useState<DeviceEutGroup>(eut)
  const [editingImage, setEditingImage] = useState<File | null>(null)
  const [editName, setEditName] = useState("")
  const [editDescription, setEditDescription] = useState("")

  useEffect(() => {
    setNewDevice(eut)
  }, [eut])

  useEffect(() => {
    const img = [...newDevice.images, ...newDevice.devices.flatMap((item) => item.images)].filter(
      (image) => image !== undefined
    )
    setImagesForSlider(img)
  }, [newDevice.images, newDevice.devices])

  const addImage = async (event: FormEvent<HTMLFormElement>, fotos: string[]) => {
    event.preventDefault()
    if (!fotos.length) {
      enqueueSnackbar("Kein Bild erfasst!", { variant: "error" })
      return
    }
    const formData = new FormData(event.currentTarget)

    const uploadImage: FileUploadInput = {
      id: "NEW",
      filename: v4() + ".png",
      name: formData.get("title") as string,
      description: (formData.get("description") as string) || undefined,
      base64: fotos[0],
      type: FileType.IMAGE,
      createdAt: new Date().toISOString(),
      createdBy: user.id,
    }

    const imageID = await uploadFile(uploadImage)

    const newImage: File = {
      id: imageID,
      createdAt: uploadImage.createdAt,
      createdBy: uploadImage.createdBy,
      fileURL: "",
      name: uploadImage.name,
      description: uploadImage.description,
      type: uploadImage.type,
      filename: uploadImage.filename,
    }
    setNewDevice({
      ...newDevice,
      images: [...newDevice.images, newImage],
    })
  }

  const handleDeleteImage = (image: File) => {
    const newData: DeviceEutGroup = {
      ...newDevice,
      images: newDevice.images.filter((item) => item.id !== image.id),
      devices: newDevice.devices.map((item) => {
        return {
          ...item,
          images: item.images.filter((item) => item.id !== image.id),
        }
      }),
    }
    setNewDevice(newData)
  }

  const handleSave = () => {
    onSave(newDevice)
    setIsEdit(false)
  }

  const handleCancel = () => {
    setNewDevice(eut)
    setIsEdit(false)
  }

  const handleEditImage = (image: File) => {
    setEditingImage(image)
    setEditName(image.name)
    setEditDescription(image.description || "")
  }

  const handleSaveImageEdit = () => {
    if (!editingImage) return

    const updateImage = (img: File): File => {
      if (img.id === editingImage.id) {
        return { ...img, name: editName, description: editDescription || undefined }
      }
      return img
    }

    const newData: DeviceEutGroup = {
      ...newDevice,
      images: newDevice.images.map(updateImage),
      devices: newDevice.devices.map((device) => ({
        ...device,
        images: device.images.map(updateImage),
      })),
    }

    setNewDevice(newData)
    setEditingImage(null)
    enqueueSnackbar("Bild aktualisiert", { variant: "success" })
  }

  return (
    <Box
      sx={{
        border: "1px solid #444",
        borderRadius: 1,
        padding: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
      }}
    >
      <Typography>Galerie</Typography>
      <Divider />

      <ImageSlider
        images={imagesForSlider}
        isDeletable={isEdit}
        isEditable={isEdit}
        onDelete={handleDeleteImage}
        onEdit={handleEditImage}
      />

      {isEdit && (
        <StyledButton
          buttonType="custom"
          customIcon={<AddAPhoto />}
          customText="Foto hinzufügen"
          fullWidth
          onClick={() => setOpenCameraDialog(true)}
          variant="contained"
        />
      )}
      <Box
        sx={{
          width: "100%",
          display: "flex",
          justifyContent: "flex-end",
          gap: 1,
        }}
      >
        {!isEdit && (
          <StyledButton
            buttonType="custom"
            customIcon={<Edit fontSize="small" />}
            onClick={() => setIsEdit(true)}
            size="small"
            variant="contained"
          />
        )}
        {isEdit && (
          <>
            <StyledButton
              buttonType="custom"
              customIcon={<Cancel fontSize="small" />}
              onClick={handleCancel}
              size="small"
              variant="contained"
            />
            <StyledButton
              buttonType="custom"
              customIcon={<Save fontSize="small" />}
              onClick={handleSave}
              size="small"
              variant="contained"
            />
          </>
        )}
      </Box>

      <CameraDialog
        onSave={addImage}
        onClose={() => setOpenCameraDialog(false)}
        open={openCameraDialog}
        imageName={eut.barcode}
      />

      <Dialog open={editingImage !== null} maxWidth="sm" fullWidth>
        <DialogTitle>Bild bearbeiten</DialogTitle>
        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, mt: 1 }}>
            <TextField
              label="Titel"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              fullWidth
              required
            />
            <TextField
              label="Beschreibung (Was zeigt das Bild?)"
              value={editDescription}
              onChange={(e) => setEditDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="z.B. Vorderansicht des Geräts, Anschlüsse, Typenschild..."
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <StyledButton
            buttonType="cancel"
            onClick={() => setEditingImage(null)}
            variant="contained"
          />
          <StyledButton
            buttonType="save"
            onClick={handleSaveImageEdit}
            variant="contained"
            disabled={!editName}
          />
        </DialogActions>
      </Dialog>
    </Box>
  )
}
